/**
 * This is exactly the same form as task.jsx, but it doesnt have the taskable
 * informations.
 *
 * It is used when we want to add a task that is attached to nothing
 */
import React from "react"
import { CustomProvider, DateRangePicker } from "rsuite"
import frFr from "rsuite/locales/fr_FR"
import {
  Heading,
  Button,
  Form,
  FormField,
  TextArea,
  TextInput,
  Select,
  SelectMultiple,
  RadioButtonGroup,
} from "grommet"
import { useLayers } from "../../contexts/layers"
import { useQuery } from "@tanstack/react-query"
import { toast } from "react-toastify"
import axios from "axios"
import {
  STATUS_TO_DO,
  STATUS_TO_CONFIRM,
  STATUS_COMPLETED,
} from "../../models/task"
import {
  useAddTaskMutation,
  useUpdateTaskMutation,
} from "../../redux/tasksSlice"
import { selectAllLeads } from "../../redux/leadsSlice"
import { selectAllCustomers } from "../../redux/customersSlice"
import { useSelector } from "react-redux"

const RESSOURCE = "tasks"

const statusOptions = [
  {
    key: STATUS_TO_DO,
    value: "A faire",
  },
  {
    key: STATUS_TO_CONFIRM,
    value: "A confirmer",
  },
  {
    key: STATUS_COMPLETED,
    value: "Terminé",
  },
]

const SingleTask = () => {
  const [add] = useAddTaskMutation()
  const [update] = useUpdateTaskMutation()
  const [layers, dispatch] = useLayers()
  const [search, setSearch] = React.useState(null)
  const [valueReady, setValueReady] = React.useState(false)
  const [value, setValue] = React.useState({
    status: statusOptions[0],
    contactable_type: "lead",
    date_range: null,
  })
  const [mode, setMode] = React.useState("simple")
  const {
    isLoading,
    isError,
    data: task,
    isSuccess,
  } = useQuery(
    ["getLayerData", RESSOURCE, layers.id, layers.relatable_type],
    () => {
      if (!layers.id) {
        return null
      }

      return axios.get(`/tasks/${layers.id}`).then(response => {
        return response.data.data.task
      })
    }
  )

  const employeesQuery = useQuery(["employees"], () => {
    return axios.get(`/employees`).then(response => {
      return response.data.data.employees.map(item => ({
        id: item.id,
        name: item.name,
      }))
    })
  })

  const suppliersQuery = useQuery(["suppliers"], () => {
    return axios.get("/suppliers").then(response => {
      return response.data.data.suppliers.map(item => ({
        id: item.id,
        name: item.company_name,
      }))
    })
  })

  const [isSubmitting, setIsSubmitting] = React.useState(false)

  React.useEffect(() => {
    if (layers.data) {
      /**
       * Loading a task while drag and dropping
       * Or getting it back from putting aside
       */
      setValue(value => ({
        ...value,
        ...layers.data,
      }))
    } else if (isSuccess && task) {
      /**
       * Updating an existing task
       */
      setValue({
        id: task.id,
        type: task.type,
        title: task.title,
        description: task.description,
        status: statusOptions.find(o => o.key === task.status),
        date_range: [new Date(task.starts_at), new Date(task.ends_at)],
        contactable_id: task.contactable?.id || task.contactable_id,
        contactable_type: task.contactable_type || "lead",
        assigned_employees:
          task?.assigned_employees?.map(item => ({
            id: item.id,
            name: item.name,
          })) || [],
        assigned_suppliers:
          task?.assigned_suppliers?.map(item => ({
            id: item.id,
            name: item.company_name,
          })) || [],
      })

      /**
       * If the task has some advanced mode fields, toggle to advanced mode immediately
       */
      if (
        task.description ||
        task.assigned_employees.length > 0 ||
        task.assigned_suppliers.length > 0
      ) {
        setMode("advanced")
      }
    }

    setValueReady(true)
  }, [task, isSuccess, layers.data])

  const options = {
    lead: useSelector(selectAllLeads),
    customer: useSelector(selectAllCustomers),
  }

  const contactableOptions = options[value.contactable_type].filter(
    o => !search || search.test(o.name)
  )

  if (isLoading || suppliersQuery.isLoading || employeesQuery.isLoading) {
    return null
  }

  if (
    isError ||
    suppliersQuery.isError ||
    employeesQuery.isError ||
    !valueReady
  ) {
    dispatch({ type: "HIDE" })
    toast.error("Erreur de chargement")
  }

  if (layers.id && !value.status) {
    return null
  }

  return (
    <>
      <div>
        <Button
          style={{ transform: "translateY(-23px)" }}
          size="xsmall"
          plain
          label="Mettre le volet de côté"
          onClick={() => {
            dispatch({ type: "PUT_ASIDE", data: value })
          }}
        />
      </div>
      <Heading level={3}>{layers.id ? "Modifier" : "Ajouter"}</Heading>
      <FormField label="Mode">
        <RadioButtonGroup
          direction="row"
          options={[
            {
              label: "Simple",
              value: "simple",
            },
            {
              label: "Avancé",
              value: "advanced",
            },
          ]}
          value={mode}
          onChange={event => setMode(event.target.value)}
        />
      </FormField>
      <Form
        data-cy={`form_${RESSOURCE}`}
        value={value}
        validate="submit"
        onChange={nextValue => {
          setValue(nextValue)
        }}
        onSubmit={({ value }) => {
          if (isSubmitting) return
          setIsSubmitting(true)

          let request
          const [starts_at, ends_at] = value.date_range
          const data = {
            ...value,
            status: value.status.key,
            assigned_employees: value.assigned_employees?.map(i => i.id),
            assigned_suppliers: value.assigned_suppliers?.map(i => i.id),
            contactable_id:
              value.contactable_id || value.contactable?.id || null,
            starts_at,
            ends_at,
          }

          if (layers.id) {
            request = update(data)
          } else {
            request = add(data)
          }

          request.unwrap().then(response => {
            dispatch({ type: "HIDE" })
            if (layers.refetch) {
              layers.refetch(response)
            }
          })
            .finally(() => setIsSubmitting(false))
        }}
      >
        <FormField label="Titre" name="title" required>
          <TextInput name="title" />
        </FormField>
        {mode === "advanced" && (
          <FormField label="Description" name="description">
            <TextArea name="description" />
          </FormField>
        )}
        <FormField label="Statut" name="status" required>
          <Select
            placeholder="Sélectionner"
            name="status"
            data-cy="status"
            options={statusOptions}
            labelKey="value"
            valueKey="key"
          />
        </FormField>
        <FormField label="Date et heure" name="date_range" required>
          <CustomProvider locale={frFr}>
            <DateRangePicker
              value={value.date_range}
              onChange={date_range => {
                setValue(value => ({ ...value, date_range }))
              }}
              placeholder="Choisissez les dates"
              cleanable={false}
              isoWeek
              ranges={[]}
              appearance="subtle"
              placement="bottomEnd"
              format="dd LLL yyyy - HH:mm"
            />
          </CustomProvider>
        </FormField>
        <FormField label="Contact" name="contactable_type">
          <RadioButtonGroup
            direction="row"
            name="contactable_type"
            options={[
              { value: "lead", label: "Prospect" },
              { value: "customer", label: "Client" },
            ]}
            value={value.contactable_type}
          />
        </FormField>
        <FormField
          name="contactable"
          label={value.contactable_type === "customer" ? "Client" : "Prospect"}
        >
          <Select
            placeholder="Sélectionner"
            name="contactable"
            data-cy="customer"
            options={contactableOptions}
            labelKey="name"
            valueKey="id"
            value={options[value.contactable_type].find(
              option =>
                option.id === value.contactable_id ||
                option.id === value.contactable?.id
            )}
            readOnly={contactableOptions.length === 0}
            clear={{ position: "top", label: "Annuler la sélection" }}
            onSearch={text => {
              if (text.length === 0) {
                setSearch(null)
                return
              }
              const escapedText = text.replace(/[-\\^$*+?.()|[\]{}]/g, "\\$&")
              setSearch(new RegExp(escapedText, "i"))
            }}
          />
        </FormField>
        {mode === "advanced" && (
          <>
            <FormField name="assigned_employees" label="Employés assignés">
              <SelectMultiple
                placeholder="Sélectionner"
                name="assigned_employees"
                data-cy="assigned_employees"
                options={employeesQuery.data}
                value={employeesQuery.data.filter(employee =>
                  value.assigned_employees?.map(i => i.id).includes(employee.id)
                )}
                labelKey="name"
                valueKey="id"
              />
            </FormField>
            <FormField name="assigned_suppliers" label="Fournisseurs assignés">
              <SelectMultiple
                placeholder="Sélectionner"
                name="assigned_suppliers"
                data-cy="assigned_suppliers"
                options={suppliersQuery.data}
                value={suppliersQuery.data.filter(supplier =>
                  value.assigned_suppliers?.map(i => i.id).includes(supplier.id)
                )}
                labelKey="name"
                valueKey="id"
              />
            </FormField>
          </>
        )}
        <div>
          <Button
            type="submit"
            primary
            reverse
            label={isSubmitting ? "Envoi en cours..." : layers.id ? "Modifier" : "Ajouter"}
            disabled={isSubmitting}
          />
        </div>
      </Form>
    </>
  )
}

export { SingleTask }
